import { useEffect, useState } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { Check, ShieldCheck, ExternalLink } from 'lucide-react';
import { db } from '../lib/firebase';
import { useAuth } from '../lib/auth';

const CHECKOUT_API = `${import.meta.env.BASE_URL}api/stripe-setup-checkout`;
const CONFIRM_API = `${import.meta.env.BASE_URL}api/stripe-confirm-pm`;

interface Props {
  demoMode: boolean;
  savedCard?: { brand?: string; last4?: string } | null;
  onSaved: () => void;
}

// Card-on-file entry. Uses Stripe's HOSTED checkout page (redirect) rather than
// embedded Elements — the embedded confirmSetup could hang indefinitely in mobile
// / in-app browsers. Stripe runs the card page, then returns to the app with
// ?card=saved, which the page-level useCardReturn() hook finalizes.
export function PaymentCardStep({ demoMode, savedCard, onSaved }: Props) {
  const { fbUser, profile } = useAuth();
  const [changing, setChanging] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  if (savedCard?.last4 && !changing) {
    return (
      <div className="card p-6 text-center">
        <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100">
          <Check className="text-emerald-700" size={24} />
        </div>
        <h2 className="font-display text-lg font-semibold text-navy-900">Card on file</h2>
        <p className="mt-2 text-sm text-ink-muted">
          {/^\d{4}$/.test(savedCard.last4 ?? '') ? (
            <>
              {savedCard.brand ? `${savedCard.brand.toUpperCase()} ` : 'Card '}ending in{' '}
              <strong>{savedCard.last4}</strong> is saved. You can continue.
            </>
          ) : (
            <>Your card is securely saved. You can continue.</>
          )}
        </p>
        <button type="button" onClick={() => setChanging(true)} className="btn-secondary mt-4 text-sm">
          Use a different card
        </button>
      </div>
    );
  }

  if (demoMode) {
    return (
      <div className="card p-6 space-y-4">
        <p className="text-sm text-ink-muted">
          A card on file is required by the lease (§5.4). In demo mode no real card is collected.
        </p>
        <div className="flex justify-end">
          <button className="btn-primary" onClick={onSaved}>
            Simulate saved card
          </button>
        </div>
      </div>
    );
  }

  async function addCard() {
    if (!fbUser) return;
    setBusy(true);
    setErr(null);
    try {
      const res = await fetch(CHECKOUT_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          uid: fbUser.uid,
          email: fbUser.email,
          name: `${profile?.personal?.firstName ?? ''} ${profile?.personal?.lastName ?? ''}`.trim(),
          customerId: profile?.stripeCustomerId,
          // Come back to this exact page; the ?card=saved is added by the server.
          returnUrl: window.location.origin + window.location.pathname,
        }),
      });
      const data = await res.json();
      if (!data.ok || !data.url) {
        setErr(data.message || 'Could not start secure card setup. Please try again.');
        setBusy(false);
        return;
      }
      // Persist the Stripe customer id so the return handler can finalize the card.
      if (db && data.customerId && data.customerId !== profile?.stripeCustomerId) {
        await updateDoc(doc(db, 'users', fbUser.uid), { stripeCustomerId: data.customerId }).catch(() => {});
      }
      window.location.href = data.url; // hand off to Stripe's hosted page
    } catch {
      setErr('Could not start secure card setup. Please try again.');
      setBusy(false);
    }
  }

  return (
    <div className="card p-6 space-y-4">
      <p className="flex items-center gap-2 text-sm text-ink-muted">
        <ShieldCheck size={16} className="text-emerald-600" />
        Your card is stored securely by Stripe. AJM never sees or stores your card number.
      </p>
      {err && <p className="text-sm text-red-700">{err}</p>}
      <button type="button" onClick={addCard} disabled={busy} className="btn-primary inline-flex items-center gap-2">
        {busy ? 'Opening secure form…' : (
          <>
            <ExternalLink size={16} /> Add card securely
          </>
        )}
      </button>
      <p className="text-xs text-ink-muted">
        Opens Stripe’s secure payment page, then brings you right back — works reliably on any phone or browser.
      </p>
      {changing && (
        <button type="button" onClick={() => setChanging(false)} className="text-xs text-ink-muted underline">
          Keep my current card
        </button>
      )}
    </div>
  );
}

export type CardReturnStatus = 'idle' | 'saved' | 'failed' | 'cancelled';

// Call at the top of any page where the card flow can return (Billing, Onboarding).
// When Stripe redirects back it reports what actually happened so the page can tell
// the user plainly — saved, cancelled, or (crucially) failed — instead of silently
// leaving them with no card. Returns the status; also fires onSaved() on success.
export function useCardReturn(onSaved?: (info: { brand?: string; last4?: string }) => void): CardReturnStatus {
  const { fbUser, profile } = useAuth();
  const [status, setStatus] = useState<CardReturnStatus>('idle');

  useEffect(() => {
    const stripParam = () => {
      const url = new URL(window.location.href);
      url.searchParams.delete('card');
      window.history.replaceState({}, '', url.pathname + url.search);
    };
    const flag = new URLSearchParams(window.location.search).get('card');
    if (!flag) return;
    if (flag === 'cancel') {
      setStatus('cancelled');
      stripParam();
      return;
    }
    if (flag !== 'saved') {
      stripParam();
      return;
    }
    const cust = profile?.stripeCustomerId;
    if (!fbUser || !cust) return; // wait until the customer id has loaded, then re-run

    let active = true;
    (async () => {
      try {
        const res = await fetch(CONFIRM_API, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ customerId: cust }),
        });
        const data = await res.json();
        if (!active) return;
        if (data.onFile) {
          if (db) {
            await updateDoc(doc(db, 'users', fbUser.uid), {
              paymentMethodOnFile: true,
              cardBrand: data.brand ?? '',
              cardLast4: data.last4 ?? '',
            }).catch(() => {});
          }
          setStatus('saved');
          onSaved?.({ brand: data.brand, last4: data.last4 });
        } else {
          // Returned from Stripe but no card actually attached — tell the user.
          setStatus('failed');
        }
      } catch {
        if (active) setStatus('failed');
      } finally {
        stripParam();
      }
    })();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fbUser, profile?.stripeCustomerId]);

  return status;
}
